import React from 'react'
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * ProcessOriginLegend (E7, §8.8) — el **interruptor** del resaltado por proceso de origen y su
 * **leyenda** (CA3.6, S86, S88).
 *
 * MUI v5, como todo componente nuevo de esta carpeta. Se monta sobre el lienzo, en
 * `WorkflowFlow.jsx`, porque el resaltado es una capa de lectura DEL GRAFO: no es una propiedad
 * del nodo seleccionado ni una sección de la paleta.
 *
 * Cuatro cosas que este componente respeta al pie de la letra (`RN-03.9`):
 *  - nace **apagado** y su estado es de **sesión del editor**: no se persiste como preferencia, y
 *    al reabrir el editor vuelve apagado;
 *  - *(D12)* lista **una entrada por PAR `(código, versión)`**, rotulada con la clave **J53**
 *    `{{code}} v{{version}}`, y el **color va por CÓDIGO**: dos versiones de la misma plantilla
 *    comparten color y se distinguen por el rótulo (S96);
 *  - los nodos **sin** proceso de origen no se colorean y **no se agrupan** en una categoría
 *    inventada: se rotulan aparte, "Without source process" (J37);
 *  - si no hay ningún nodo con origen, el interruptor se enciende igual, no colorea nada y la
 *    leyenda **lo declara** con esa misma fila (S88).
 *
 * Solo exige `WORKFLOWS`: el proceso de origen es un dato del nodo del workflow, no de la
 * biblioteca, así que sin `OPERATION_LIBRARY` el resaltado sigue disponible (S89, `RN-G16`).
 *
 * @param {Object} props
 * @param {boolean} props.enabled Si el resaltado está encendido
 * @param {function(boolean)} props.onEnabledChange
 * @param {{entries: {processCode: string, versionNumber: number|null, color: string}[],
 *   hasNodesWithoutOrigin: boolean, anyOrigin: boolean}} props.legend Lo que
 *   `processOriginHighlight.js#buildProcessOriginLegend` resuelve sobre los nodos del lienzo
 */
const ProcessOriginLegend = ({ enabled = false, onEnabledChange = () => {}, legend = {} }) => {
  const { t_ } = useSmartTranslation()
  const entries = legend?.entries || []

  return (
    <Paper elevation={3} sx={{ padding: 1, opacity: 0.95 }}>
      <FormControlLabel
        data-testid='process-origin-toggle'
        control={<Switch size='small' checked={enabled} onChange={(event) => onEnabledChange(event.target.checked)} />}
        label={<Typography variant='caption'>{t_('Highlight by source process')}</Typography>}
      />
      {enabled
        ? (
          <Stack data-testid='process-origin-legend' spacing={0.25} sx={{ paddingLeft: 1.5 }}>
            <Typography variant='caption' color='primary'>{t_('Process')}</Typography>
            {entries.map((entry) => (
              <Stack
                key={entry.processCode + '-v' + entry.versionNumber}
                data-testid={'process-origin-legend-' + entry.processCode + '-v' + entry.versionNumber}
                direction='row'
                alignItems='center'
                spacing={0.75}
              >
                <Box sx={{ width: '0.75rem', height: '0.75rem', borderRadius: '2px', backgroundColor: entry.color }} />
                {/* D12 (J53, `RN-03.9`): el rótulo es el PAR, resuelto con UNA clave con
                    interpolación — la "v" y el orden son traducibles, no se concatenan trozos. Dos
                    versiones del mismo proceso dan dos entradas del MISMO color (S96). */}
                <Typography variant='caption'>
                  {t_('{{code}} v{{version}}', { code: entry.processCode, version: entry.versionNumber })}
                </Typography>
              </Stack>
            ))}
            {/* J37: los nodos que NO se colorean se nombran; nunca se les inventa un color. */}
            {legend?.hasNodesWithoutOrigin
              ? <Typography variant='caption' color='text.secondary'>{t_('Without source process')}</Typography>
              : null}
          </Stack>
          )
        : null}
    </Paper>
  )
}

export default ProcessOriginLegend
